import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Droplet, Fan, Snowflake, Flame } from 'lucide-react';
import { Mode } from '@/types';
import { useEnumMappings } from '@/contexts/enum-mappings-context';
import { useAHUControl } from '@/contexts/ahu-control-context';

interface ModeControlProps {
  onChange: (mode: Mode) => void;
  ahuMode: Mode | null;
}

export function ModeControl({ onChange, ahuMode }: ModeControlProps) {
  const { enumMappings } = useEnumMappings();
  const { setMode: setContextMode } = useAHUControl();

  // Ensure ahuMode is lowercase to handle capitalized enum values from API
  // Also convert 'auto' to 'auto cool'
  const normalizedAhuMode = ahuMode && typeof ahuMode === 'string' ?
    (ahuMode.toLowerCase() === 'auto' ? 'auto cool' : ahuMode.toLowerCase()) as Mode : ahuMode;

  const [mode, setMode] = useState<Mode | null>(normalizedAhuMode);

  // Get available modes from enumMappings or fall back to defaults
  const availableModes = useMemo(() => {
    // If we have enum mappings from the backend, use them
    if (Object.values(enumMappings.mode).length > 0) {
      console.log('Mode enum mappings:', enumMappings.mode);

      // Get unique mode values from the enum mappings
      const modes = Object.values(enumMappings.mode)
        .map(value => {
          if (typeof value !== 'string') return value;
          // Convert 'auto' to 'auto cool'
          const lowerValue = value.toLowerCase();
          return lowerValue === 'auto' ? 'auto cool' : lowerValue;
        })
        .filter((value, index, self) =>
          // Filter out invalid, unfix, and duplicates
          value &&
          value !== 'invalid' &&
          value !== 'unfix' &&
          self.indexOf(value) === index
        ) as Mode[];

      console.log('Filtered modes from enum mappings:', modes);

      // If we have valid modes from the backend, use them
      if (modes.length > 0) {
        // Ensure we have at least the basic modes
        const essentialModes = ['cool', 'fan', 'dry'];
        const missingModes = essentialModes.filter(mode => !modes.includes(mode as Mode));

        if (missingModes.length > 0) {
          console.log('Adding missing essential modes:', missingModes);
          return [...modes, ...missingModes] as Mode[];
        }

        return modes;
      }
    }

    // Fall back to defaults if no valid modes from backend
    console.log('Using default modes');
    return ['cool', 'fan', 'dry'] as Mode[];
  }, [enumMappings.mode]);

  // Function to get the appropriate icon for a mode
  const getModeIcon = (type: Mode) => {
    // Convert 'auto' to 'auto cool'
    let normalizedType = type.toLowerCase();
    if (normalizedType === 'auto') normalizedType = 'auto cool';

    switch (normalizedType) {
      case 'cool': return <Snowflake className={cn("w-[16px] h-[16px]", (mode && mode.toLowerCase() === normalizedType) ? "text-white" : "text-primary")} />;
      case 'fan': return <Fan className={cn("w-[16px] h-[16px]", (mode && mode.toLowerCase() === normalizedType) ? "text-white" : "text-primary")} />;
      case 'dry': return <Droplet className={cn("w-[16px] h-[16px]", (mode && mode.toLowerCase() === normalizedType) ? "text-white" : "text-primary")} />;
      case 'heat': return <Flame className={cn("w-[16px] h-[16px]", (mode && mode.toLowerCase() === normalizedType) ? "text-white" : "text-primary")} />;
      case 'auto cool': return <Snowflake className={cn("w-[16px] h-[16px]", (mode && mode.toLowerCase() === normalizedType) ? "text-white" : "text-primary")} />;
      case 'auto heat': return <Flame className={cn("w-[16px] h-[16px]", (mode && mode.toLowerCase() === normalizedType) ? "text-white" : "text-primary")} />;
      default: return <Snowflake className={cn("w-[16px] h-[16px]", (mode && mode.toLowerCase() === normalizedType) ? "text-white" : "text-primary")} />;
    }
  };

  return (
    <div className="px-2 pt-[6px] pb-[10px] bg-bg rounded-lg border border-light-grey flex flex-col gap-1">
      <div className="text-dark-blue heading-7">Mode</div>
      <div className="flex gap-1.5 flex-wrap">
        {availableModes.map((type) => (
          <button
            key={type}
            onClick={() => {
              setMode(type)
              setContextMode(type) // Update the context
              onChange(type)
            }}
            className={cn(
              "flex flex-col items-center gap-0.5",
              "w-7 text-[11px]",
              // Case-insensitive comparison to handle capitalized enum values
              (mode && mode.toLowerCase() === type.toLowerCase()) ? "text-primary" : "text-dark-grey"
            )}
          >
            <div className={cn(
              "w-7 h-7 flex items-center justify-center rounded-md",
              // Case-insensitive comparison to handle capitalized enum values
              (mode && mode.toLowerCase() === type.toLowerCase())
                ? "bg-primary/10 shadow-popup bg-alto"
                : "bg-white"
            )}>
              <div className={cn(
                "w-4 h-4",
                // Case-insensitive comparison to handle capitalized enum values
                (mode && mode.toLowerCase() === type.toLowerCase()) ? "text-primary" : "text-dark-grey"
              )}>
                {getModeIcon(type)}
              </div>
            </div>
            <span className="paragraph-11 capitalize">{type}</span>
          </button>
        ))}
      </div>
    </div>
  );
}